import { InputHTMLAttributes, forwardRef } from 'react'
import { clsx } from 'clsx'
import { PhoneIcon } from '@heroicons/react/24/outline'
import { useTranslation } from '../../core/hooks/useTranslation'

interface PhoneNumberInputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type' | 'value' | 'onChange'> {
  label?: string
  error?: string
  helperText?: string
  value: string
  onChange: (value: string) => void
  defaultCountryCode?: string // e.g., '+20'
  required?: boolean
}

const PhoneNumberInput = forwardRef<HTMLInputElement, PhoneNumberInputProps>(
  ({ 
    className,
    label,
    error,
    helperText,
    value,
    onChange,
    defaultCountryCode = '+20',
    required,
    disabled,
    id,
    ...props
  }, ref) => {
    const { isRTL } = useTranslation()
    const inputId = id || `phone-input-${Math.random().toString(36).substr(2, 9)}`
    
    // Format phone number - keep only digits
    const formatPhoneNumber = (phone: string): string => {
      // Remove all non-digit characters (keep only numbers)
      return phone.replace(/\D/g, '')
    }
    
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const formatted = formatPhoneNumber(e.target.value)
      onChange(formatted)
    }
    
    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      // Clean the value on blur
      if (value) {
        onChange(formatPhoneNumber(value))
      }
      props.onBlur?.(e)
    }
    
    const displayValue = value || ''

    return (
      <div className="space-y-2">
        {label && (
          <label 
            htmlFor={inputId} 
            className="block text-sm text-gray-700 mb-1"
          >
            {label}
            {required && <span className="text-danger-500 mr-1">*</span>}
          </label>
        )}
        
        <div className="relative">
          {/* Phone Icon */}
          <div className={`absolute inset-y-0 ${isRTL ? 'right-0 pr-3' : 'left-0 pl-3'} flex items-center pointer-events-none z-10`}>
            <span className={clsx(
              'text-gray-400',
              isRTL && 'scale-x-[-1]'
            )}>
              <PhoneIcon className="w-5 h-5" />
            </span>
          </div>
          
          {/* Input Container */}
          <div className={clsx(
            'relative',
            isRTL && 'flex justify-end'
          )}>
            <input
              id={inputId}
              type="tel"
              dir="ltr"
              value={displayValue}
              onChange={handleChange}
              onBlur={handleBlur}
              disabled={disabled}
              className={clsx(
                'input',
                error && 'input-error',
                isRTL ? 'pr-10 text-right' : 'pl-10',
                className
              )}
              ref={ref}
              placeholder={isRTL ? '01234567890' : '01234567890'}
              {...props}
            />
          </div>
        </div>
        
        {error && (
          <div className="flex items-center space-x-1 rtl:space-x-reverse">
            <span className="text-sm text-danger-600 font-medium">{error}</span>
          </div>
        )}
        
        {helperText && !error && (
          <p className="text-sm text-secondary-500">{helperText}</p>
        )}
      </div>
    )
  }
)

PhoneNumberInput.displayName = 'PhoneNumberInput'

export default PhoneNumberInput

